import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, X, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";

const KIND_STYLES = {
  shift_reminder: "bg-emerald-100 text-emerald-800",
  mark_completion_nudge: "bg-amber-100 text-amber-800",
  shift_cancelled: "bg-rose-100 text-rose-800",
  cover_applicant: "bg-indigo-100 text-indigo-800",
};

export default function NotificationsBell() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch (err) {
      // Silent when unauthenticated on public routes; log everything else.
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("notifications load failed:", err.response.status);
      }
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => { if (open && panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    await load();
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    await load();
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen(v => !v)}
        className="relative p-2 rounded-full hover:bg-slate-100"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {data.unread > 0 && (
          <span data-testid="notifications-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-semibold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[min(92vw,340px)] bg-white border border-slate-200 rounded-2xl shadow-lg overflow-hidden z-50" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="font-display font-semibold">Notifications</div>
            <div className="flex items-center gap-1">
              {data.unread > 0 && (
                <button onClick={markAll} className="text-xs text-emerald-800 hover:underline flex items-center gap-1" data-testid="notifications-mark-all">
                  <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-slate-100"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {data.items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">You&apos;re all caught up.</div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
              {data.items.map(n => {
                const isCoverApp = n.kind === "cover_applicant";
                const waPhone = (n.meta?.applicant_phone || "").replace(/\D/g, "");
                const waMsg = encodeURIComponent(`Hi, thanks for applying for the shift. ${n.title}`);
                const waHref = waPhone ? `https://wa.me/${waPhone}?text=${waMsg}` : null;
                return (
                  <div
                    key={n.notification_id}
                    className={"px-4 py-3 hover:bg-slate-50 " + (n.read_at ? "opacity-60" : "")}
                    data-testid={`notification-${n.notification_id}`}
                  >
                    <button
                      onClick={() => markOne(n.notification_id)}
                      className="w-full text-left"
                      aria-label="Mark read"
                    >
                      <div className="flex items-start gap-3">
                        <span className={"mt-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-medium " + (KIND_STYLES[n.kind] || "bg-slate-100 text-slate-700")}>
                          {n.kind === "shift_reminder" ? "Reminder" : n.kind === "shift_cancelled" ? "Cancelled" : n.kind === "cover_applicant" ? "New applicant" : "Nudge"}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm text-slate-900">{n.title}</div>
                          <div className="text-xs text-slate-600 mt-0.5 break-words">{n.body}</div>
                        </div>
                      </div>
                    </button>
                    {isCoverApp && waHref && (
                      <a
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => markOne(n.notification_id)}
                        data-testid={`notification-wa-${n.notification_id}`}
                        className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white text-xs font-medium px-3 py-1.5"
                      >
                        Open WhatsApp
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
